import React, { useEffect, useReducer } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';

import { REAL_TIME_SCENARIO, utils } from 'state';
import type { MarketHoursType, Session, UnderlyingAsset } from 'state';

type MarketHoursStatusArgs = {
  asset?: UnderlyingAsset;
  session: Session;
  marketHours?: MarketHoursType;
  enabled?: boolean;
};

export const useMarketClosed = ({
  asset,
  session,
  marketHours,
  enabled = true,
}: MarketHoursStatusArgs): boolean => {
  const [, refresh] = useReducer((tick: number) => tick + 1, 0);
  const hasSchedule =
    enabled &&
    asset !== undefined &&
    session.scenario_type === REAL_TIME_SCENARIO &&
    asset.resourcetype !== 'Crypto' &&
    marketHours !== undefined;

  useEffect(() => {
    if (!hasSchedule) return;

    // Recheck the loaded schedule locally; no requests or Redux dispatches.
    const interval = window.setInterval(refresh, 60_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [hasSchedule]);

  return (
    hasSchedule &&
    !utils.assetIsOpenNew(asset, session, marketHours, {
      includeClosingMinute: true,
    })[0]
  );
};

const MarketClosedLabel: React.FC<{ closed: boolean }> = ({ closed }) => {
  if (!closed) return null;

  return (
    <span className="markets-v5-market-closed">
      <ClockCircleOutlined aria-hidden="true" />
      Market closed
    </span>
  );
};

export default MarketClosedLabel;
